import { put, takeEvery, takeLatest, call } from 'redux-saga/effects'
import { AsyncAction } from 'src/utils/reduxUtils'
import { captureSentryError } from 'src/utils/sentry'
import {
  CreatePriorityQueueContact,
  ExportPriorityQueueCsv,
  FetchPriorityQueue,
  FetchPriorityQueueDetail,
  FetchPriorityQueueFilterOptions,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import {
  Contact,
  CreateContactParams,
  PriorityQueueDetail,
  PriorityQueueFilterOptions,
  PriorityQueueListResponse,
} from 'src/components/monitoring/types'
import type { ExportPriorityQueueCsvPayload, FetchPriorityQueuePayload } from './monitoring.types'
import { callSecureApi } from './api/callApi'

function* onFetchPriorityQueue(
  action: AsyncAction<FetchPriorityQueuePayload, PriorityQueueListResponse>,
) {
  try {
    const { payload }: { payload: PriorityQueueListResponse } = yield call(
      callSecureApi,
      Endpoints.FetchPriorityQueue,
      { query: { ...action.payload } },
    )

    yield put(FetchPriorityQueue.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch the priority queue'

    captureSentryError(err, { message: '[saga.monitoring.onFetchPriorityQueue] error' })
    yield put(FetchPriorityQueue.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchPriorityQueueFilterOptions(action: AsyncAction<void, PriorityQueueFilterOptions>) {
  try {
    const { payload }: { payload: PriorityQueueFilterOptions } = yield call(
      callSecureApi,
      Endpoints.FetchPriorityQueueFilterOptions,
    )

    yield put(FetchPriorityQueueFilterOptions.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch the priority queue filters'

    captureSentryError(err, {
      message: '[saga.monitoring.onFetchPriorityQueueFilterOptions] error',
    })
    yield put(FetchPriorityQueueFilterOptions.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onFetchPriorityQueueDetail(
  action: AsyncAction<{ contractorId: string }, PriorityQueueDetail>,
) {
  try {
    const { payload }: { payload: PriorityQueueDetail } = yield call(
      callSecureApi,
      Endpoints.FetchPriorityQueueDetail,
      { params: [{ field: ':contractorId', value: action.payload.contractorId }] },
    )

    yield put(FetchPriorityQueueDetail.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to fetch the contractor'

    captureSentryError(err, { message: '[saga.monitoring.onFetchPriorityQueueDetail] error' })
    yield put(FetchPriorityQueueDetail.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onCreatePriorityQueueContact(action: AsyncAction<CreateContactParams, Contact>) {
  try {
    const { contractorId, channel, note } = action.payload

    const { payload }: { payload: Contact } = yield call(
      callSecureApi,
      Endpoints.CreatePriorityQueueContact,
      {
        method: 'POST',
        params: [{ field: ':contractorId', value: contractorId }],
        body: { channel, note },
      },
    )

    yield put(CreatePriorityQueueContact.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to log the contact'

    captureSentryError(err, { message: '[saga.monitoring.onCreatePriorityQueueContact] error' })
    yield put(CreatePriorityQueueContact.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

function* onExportPriorityQueueCsv(
  action: AsyncAction<ExportPriorityQueueCsvPayload, ArrayBuffer>,
) {
  try {
    const { exportColumns, ...filters } = action.payload

    const { payload }: { payload: ArrayBuffer } = yield call(
      callSecureApi,
      Endpoints.ExportPriorityQueueCsv,
      {
        method: 'POST',
        body: { ...filters, exportColumns },
        returnBuffer: true,
      },
    )

    yield put(ExportPriorityQueueCsv.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    const errorMessage = err?.payload?.message || 'Failed to export the priority queue'

    captureSentryError(err, { message: '[saga.monitoring.onExportPriorityQueueCsv] error' })
    yield put(ExportPriorityQueueCsv.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* monitoringSaga() {
  yield takeLatest(FetchPriorityQueue.type.REQUEST, onFetchPriorityQueue)
  yield takeLatest(FetchPriorityQueueFilterOptions.type.REQUEST, onFetchPriorityQueueFilterOptions)
  yield takeLatest(FetchPriorityQueueDetail.type.REQUEST, onFetchPriorityQueueDetail)
  yield takeEvery(CreatePriorityQueueContact.type.REQUEST, onCreatePriorityQueueContact)
  yield takeEvery(ExportPriorityQueueCsv.type.REQUEST, onExportPriorityQueueCsv)
}
